import Promise from 'bluebird';
import _ from 'underscore';
import { ResponsiveAd } from '@bingads-webui/campaign-odata-schemas';
import { newVideoFromMTObject } from '@bingads-webui-campaign-react/image-experience-video-part';
import { edmCampaign, getEntityFromScope } from '@bingads-webui/edm-campaign';
import { QueryParam } from '@bingads-webui-react/scope-service';

import { isBulkWrappedResponse, getBulkUploadErrorType, modelToViewModelForBulkErrors, ERROR_ENTITY } from '../common';
import { bulkModelToViewModelForAd, splitUpdateAndCreateAds } from './ad';

const attachUidToErrorProperty = errors => _.map(errors, (error) => {
  // eslint-disable-next-line no-param-reassign
  error.Property = `${error.Property}_${error['Client Id']}`;

  return error;
});

const GENERIC_ERROR_MESSAGE_KEY = _TL_("We've encountered an error and can't continue. Please contact support for further assistance.");

const adEDM = edmCampaign.types.resolve('Model.ResponsiveAd');
const adResource = new adEDM.ResourceIdentifier();


const createData = data => adResource.bulkCreate.bind(adResource)(data);

const updateData = data => adResource.bulkUpdate.bind(adResource)(data);

const bulkUpsertAdSettingsEntityErrorMap = {
  [getBulkUploadErrorType(ResponsiveAd)]: ERROR_ENTITY.Ad,
};

export const getODataConfig = (serverContext) => {
  const isLocal = () => {
    const hostname = document.location.hostname.toLowerCase();
    return hostname.indexOf('ads.microsoft.com') === -1 &&
        hostname.indexOf('ads-int.microsoft.com') === -1;
  };

  const odataPath = isLocal() ? 'Advertiser/V2' : 'Campaign/V2';

  const oDataConfig = {
    odataToken: serverContext.Token,
    oDataToken: serverContext.Token,
    odataPath: `${serverContext.ServicesEndpoints.CampaignAPI}${odataPath}`,
    oDataPath: `${serverContext.ServicesEndpoints.CampaignAPI}${odataPath}`,
  };

  return oDataConfig;
};

export const getErrorsPlaceholders = () => ({
  systemErrors: [],
  serverErrors: [],
});

const postVideoByUrl = ({ videoUrl }) => {
  const queryParam = new QueryParam(window.location);

  queryParam.remove(['adGroupId', 'campaignId']);
  const { currentEntity } = getEntityFromScope(queryParam);

  return currentEntity
    .Videos['Default.CreateVideo']
    .$makeCall({
      functionParams: {},
      payload: {
        videoUrl,
        description: '',
      },
    })
    .toPromise();
};

const uploadVideo = ({ ad }) => postVideoByUrl({ videoUrl: ad.data.video.url }).then((res) => {
  _.extend(ad.data.video, newVideoFromMTObject({ SubType: 'VideoMedia16X9', Status: 'Draft', Id: res.value }));
});

const uploadVideoIfNeeded = ({ ads }) => {
  const videoPromises = [];
  ads.data.forEach((ad) => {
    if (ad.data && ad.data.video && !ad.data.video.id) {
      videoPromises.push(uploadVideo({ ad }));
    }
  });

  if (_.isEmpty(videoPromises)) {
    return Promise.resolve(ads);
  }

  return Promise.all(videoPromises).then(() => ads);
};

export const saveAds = ({
  ads,
  oldAdsModel,
  campaign,
  adGroup,
  activity = null,
}, { scenario, i18n }) => uploadVideoIfNeeded({ ads }).then((adsViewModel) => {
  const { adsNeededToCreate, adsNeededToUpdate } = splitUpdateAndCreateAds({
    adsViewModel, oldAdsModel, campaign, adGroup,
  });

  const promises = {};

  if (adsNeededToCreate.length > 0) {
    promises.createAds = createData(adsNeededToCreate);
  }

  if (adsNeededToUpdate.length > 0) {
    promises.updateAds = updateData(adsNeededToUpdate);
  }

  return Promise.props(promises).then((results) => {
    const updatedAds = results.updateAds || [];
    const createdAds = results.createAds || [];

    const result = [...updatedAds, ...createdAds];

    const data = {
      ads: bulkModelToViewModelForAd({
        adsModel: result,
        originalAdsModel: adsViewModel,
      }),
      ...getErrorsPlaceholders(),
    };


    if (activity) {
      activity.signal(`onAdsSavedSuccessfully, redirecting to campaign grid page - adCount: ${result.length}`);
    }
    return data;
  }, (bulkOrSystemError) => {
    if (isBulkWrappedResponse(bulkOrSystemError)) {
      const { entity, errors } = bulkOrSystemError;

      // we need to attach uid to each error to identify which video panel has an error.
      // the uid is stored in the clientid of the error.
      const errorsWithPropertyIdsAttached = attachUidToErrorProperty(errors);

      const entities = entity ?
        bulkModelToViewModelForAd({
          adsModel: entity,
          originalAdsModel: adsViewModel,
        }) :
        adsViewModel;

      const serverErrors = modelToViewModelForBulkErrors(
        errorsWithPropertyIdsAttached,
        bulkUpsertAdSettingsEntityErrorMap,
        scenario,
        i18n,
        activity
      );

      if (activity) {
        activity.signal(`onAdsSavedUnsuccessfully, ${JSON.stringify(serverErrors)}`);
      }
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        ads: { ...entities },
        serverErrors,
      });
    }

    // Activity
    // eslint-disable-next-line no-console
    console.warn(`Campaign bulk upsert system error: ${bulkOrSystemError.stack || bulkOrSystemError.message || bulkOrSystemError}`);
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      ads: adsViewModel,
      systemErrors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
      // TODO: We would want to return the criterions with Ids, but UI's bulk drops them
    });
  });
});
